export type WebpushObjectType = 'timeentry' | 'timeslot'

export type WebpushChangedObject = {
  type: WebpushObjectType
  id: unknown
  item: unknown
}

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null

const normalizeObjectType = (type: unknown): WebpushObjectType | null => {
  const normalized = String(type || '')
    .toLowerCase()
    .replace(/[-_\s]/g, '')

  if (normalized === 'timeslot' || normalized === 'slot') return 'timeslot'
  if (normalized === 'timeentry' || normalized === 'entry') return 'timeentry'
  return null
}

const getObjectId = (value: unknown): unknown => {
  if (!isRecord(value)) return value ?? null

  return (
    value.id ??
    value.value ??
    value.object_id ??
    value.objectId ??
    value.timeentry_id ??
    value.timeentryId ??
    value.time_entry_id ??
    null
  )
}

export const getFallbackTypeFromAction = (
  action: unknown
): WebpushObjectType | null => {
  const normalizedAction = String(action || '').toLowerCase()
  const [prefix, event] = normalizedAction.split('.')

  if (prefix === 'clocking') return 'timeslot'
  if (
    prefix === 'status' &&
    ['release', 'released', 'accept', 'accepted'].includes(event)
  ) {
    return 'timeentry'
  }

  return normalizeObjectType(prefix)
}

export const extractChangedObjects = (
  item: unknown,
  fallbackType: WebpushObjectType | null = null
): WebpushChangedObject[] => {
  const itemRecord = isRecord(item) ? item : {}
  const changedObjects: WebpushChangedObject[] = []
  const changedObjectKeys = new Set<string>()

  const addObject = (type: unknown, value: unknown) => {
    const normalizedType = normalizeObjectType(type)
    const id = getObjectId(value)
    if (!normalizedType || id == null) return

    const key = `${normalizedType}:${String(id)}`
    if (changedObjectKeys.has(key)) return

    changedObjectKeys.add(key)
    changedObjects.push({
      type: normalizedType,
      id,
      item: isRecord(value) ? value : item,
    })
  }

  addObject(
    'timeslot',
    itemRecord.slot ?? itemRecord.timeslot ?? itemRecord.time_slot
  )
  addObject(
    'timeentry',
    itemRecord.timeentry ?? itemRecord.time_entry ?? itemRecord.entry
  )
  addObject(itemRecord.key, itemRecord.value)
  addObject(
    itemRecord.type ?? itemRecord.object_type ?? itemRecord.objectType,
    itemRecord.id ??
      itemRecord.object_id ??
      itemRecord.objectId ??
      itemRecord.value
  )
  addObject(fallbackType, item)

  return changedObjects
}

export const parseWebpushChangedObjects = (
  action: unknown,
  rawItems: unknown
): WebpushChangedObject[] => {
  const items = Array.isArray(rawItems)
    ? rawItems
    : rawItems != null
      ? [rawItems]
      : []
  const fallbackType = getFallbackTypeFromAction(action)

  return items.flatMap(item => extractChangedObjects(item, fallbackType))
}
